import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import "./AdminProducts.css";

const API_URL =
  "https://etoile-bricolage-api.imanehibouzid2001.workers.dev";

function AdminProducts() {
  const navigate = useNavigate();

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [deletingId, setDeletingId] = useState(null);
  const [loggingOut, setLoggingOut] = useState(false);

  const loadProducts = async () => {
    try {
      setLoading(true);
      setError("");

      const response = await fetch(`${API_URL}/api/products`);

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(
          data.error || "Impossible de récupérer les produits."
        );
      }

      setProducts(data.products || []);
    } catch (err) {
      console.error(err);
      setError(err.message || "Une erreur est survenue.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProducts();
  }, []);

  // ====================================================
  // DECONNEXION ADMIN
  // ====================================================

  const handleLogout = async () => {
    try {
      setLoggingOut(true);
      setError("");

      const response = await fetch(
        `${API_URL}/api/admin/logout`,
        {
          method: "POST",
          credentials: "include",
        }
      );

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(
          data.error || "Impossible de se déconnecter."
        );
      }

      navigate("/admin/login", {
        replace: true,
      });
    } catch (err) {
      console.error("Erreur déconnexion :", err);

      setError(
        err.message ||
          "Une erreur est survenue lors de la déconnexion."
      );
    } finally {
      setLoggingOut(false);
    }
  };

  // ====================================================
  // SUPPRESSION PRODUIT
  // ====================================================

  const handleDelete = async (product) => {
    const productLabel =
      product.name && product.name.trim()
        ? `"${product.name}"`
        : "ce produit";

    const confirmation = window.confirm(
      `Voulez-vous vraiment supprimer ${productLabel} ?`
    );

    if (!confirmation) {
      return;
    }

    try {
      setDeletingId(product.id);
      setError("");

      const response = await fetch(
        `${API_URL}/api/products/${product.id}`,
        {
          method: "DELETE",
          credentials: "include",
        }
      );

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(
          data.error || "Impossible de supprimer le produit."
        );
      }

      setProducts((currentProducts) =>
        currentProducts.filter(
          (currentProduct) =>
            currentProduct.id !== product.id
        )
      );
    } catch (err) {
      console.error(err);

      setError(
        err.message ||
          "Erreur lors de la suppression."
      );
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <main className="admin-products-page">
      <div className="admin-products-container">

        <div className="admin-products-header">
          <div>
            <p className="admin-products-brand">
              ÉTOILE BRICOLAGE
            </p>

            <h1>Gestion des produits</h1>

            <p className="admin-products-subtitle">
              Ajoutez, modifiez ou supprimez les produits
              de votre catalogue.
            </p>
          </div>

          <div className="admin-header-actions">

            <Link
              to="/admin/products/create"
              className="admin-add-product-button"
            >
              + Ajouter un produit
            </Link>

            <button
              type="button"
              className="admin-logout-button"
              onClick={handleLogout}
              disabled={loggingOut}
            >
              {loggingOut
                ? "Déconnexion..."
                : "Se déconnecter"}
            </button>

          </div>
        </div>

        {error && (
          <div className="admin-products-error">
            {error}
          </div>
        )}

        {loading ? (
          <div className="admin-products-message">
            Chargement des produits...
          </div>
        ) : products.length === 0 ? (
          <div className="admin-products-empty">

            <div className="admin-products-empty-icon">
              📦
            </div>

            <h2>Aucun produit</h2>

            <p>
              Aucun produit n'a encore été ajouté au
              catalogue.
            </p>

            <Link
              to="/admin/products/create"
              className="admin-empty-add-button"
            >
              Ajouter mon premier produit
            </Link>

          </div>
        ) : (
          <div className="admin-products-grid">

            {products.map((product) => (
              <article
                className="admin-product-card"
                key={product.id}
              >

                <div className="admin-product-image-wrapper">
                  <img
                    src={product.image_url}
                    alt={
                      product.name ||
                      "Produit Étoile Bricolage"
                    }
                    className="admin-product-image"
                  />
                </div>

                <div className="admin-product-content">

                  <span className="admin-product-category">
                    {product.category_name ||
                      "Sans catégorie"}
                  </span>

                  <h2>
                    {product.name &&
                    product.name.trim()
                      ? product.name
                      : "Produit sans nom"}
                  </h2>

                  <p className="admin-product-description">
                    {product.description &&
                    product.description.trim()
                      ? product.description
                      : "Aucune description"}
                  </p>

                  <div className="admin-product-actions">

                    <Link
                      to={`/admin/products/edit/${product.id}`}
                      className="admin-edit-button"
                    >
                      Modifier
                    </Link>

                    <button
                      type="button"
                      className="admin-delete-button"
                      onClick={() =>
                        handleDelete(product)
                      }
                      disabled={
                        deletingId === product.id
                      }
                    >
                      {deletingId === product.id
                        ? "Suppression..."
                        : "Supprimer"}
                    </button>

                  </div>
                </div>

              </article>
            ))}

          </div>
        )}

      </div>
    </main>
  );
}

export default AdminProducts;